// PaymentSuccessRedirect.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import httpClient from '../axios';

const PaymentSuccessRedirect = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Verify payment with backend
  const verifyPaymentWithBackend = async () => {
    try {
      console.log('Verifying payment with backend for order:', orderId);

      const response = await httpClient.get(
        `/api/payment-requests/verify/${orderId}`
      );

      console.log('Payment verification result:', response.data);

    //   if (response.data.success) {
    //     console.log('Payment successfully verified with backend');

    //     // Update payment data with backend verification result
    //     const updatedPaymentData = {
    //       ...response.data,
    //       backendVerified: true,
    //       backendStatus: response.data.paymentStatus,
    //       verificationResponse: response.data,
    //     };

    //     return updatedPaymentData;
    //   } else {
    //     console.warn('Backend verification failed:', response.data.message);
    //     return { 
    //       success: false, 
    //       message: response.data.message,
    //       backendVerified: false 
    //     };
    //   }
    } catch (error) {
      console.error('Error verifying payment with backend:', error);
      return { 
        success: false, 
        message: 'Verification failed',
        backendVerified: false,
        error: error.message 
      };
    }
  };

  useEffect(() => {
    const verifyPayment = async () => {
      if (!orderId) {
        setVerificationStatus('error');
        setError('Order ID is missing');
        return;
      }

      try {
        setVerificationStatus('verifying');
        const result = await verifyPaymentWithBackend();
        
        if (result.backendVerified) {
          setVerificationStatus('success');
          setPaymentData(result);
        } else {
          setVerificationStatus('failed');
          setError(result.message || 'Payment verification failed');
        }
      } catch (err) {
        setVerificationStatus('error');
        setError('An unexpected error occurred');
      }
    };

    verifyPayment();
  }, [orderId]);

  // Countdown for automatic redirect
  useEffect(() => {
    if (verificationStatus === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (verificationStatus === 'success' && countdown === 0) {
      // Redirect to success page or orders page
      navigate('/orders', { replace: true });
    }
  }, [verificationStatus, countdown, navigate]);

  const handleManualRedirect = () => {
    navigate('/orders', { replace: true });
  };

  const handleRetryVerification = async () => {
    setVerificationStatus('verifying');
    setError(null);
    
    try {
      const result = await verifyPaymentWithBackend();
      
      if (result.backendVerified) {
        setVerificationStatus('success');
        setPaymentData(result);
      } else {
        setVerificationStatus('failed');
        setError(result.message || 'Payment verification failed');
      }
    } catch (err) {
      setVerificationStatus('error');
      setError('An unexpected error occurred');
    }
  };

  // Loading state
  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Verifying Payment
          </h2>
          <p className="text-gray-600 mb-2">
            Please wait while we verify your payment...
          </p>
          <p className="text-sm text-gray-500">
            Order ID: {orderId}
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Payment Successful! 🎉
          </h2>
          
          <p className="text-gray-600 mb-6">
            Your payment has been verified successfully. You will be redirected automatically.
          </p>

          {/* Order Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-800 mb-3">Order Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Order ID:</span>
                <span className="font-medium">{orderId}</span>
              </div>
              {paymentData?.amount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-green-600">
                    ₹{paymentData.amount}
                  </span>
                </div>
              )}
              {paymentData?.transactionId && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="font-medium text-xs">{paymentData.transactionId}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-green-600">Verified ✅</span>
              </div>
            </div>
          </div>

          {/* Countdown */}
          <div className="mb-6">
            <p className="text-gray-600 text-sm">
              Redirecting to orders page in{' '}
              <span className="font-bold text-green-600">{countdown}</span> seconds
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleManualRedirect}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              Go to Orders Now
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error/Failed state
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {/* Error Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          Verification {verificationStatus === 'failed' ? 'Failed' : 'Error'}
        </h2>
        
        <p className="text-gray-600 mb-4">
          {error || 'We encountered an issue verifying your payment.'}
        </p>
 
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <h3 className="font-semibold text-gray-800 mb-2">Order Reference</h3>
          <p className="text-sm text-gray-600">Order ID: {orderId}</p>
        </div>

        {/* Action Buttons */}
        {/* <div className="space-y-3">
          <button
            onClick={handleRetryVerification}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
          >
            Retry Verification
          </button>
          <button
            onClick={() => navigate('/support')}
            className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-lg transition duration-200"
          >
            Contact Support
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full text-gray-600 hover:text-gray-800 font-semibold py-2 transition duration-200"
          >
            Back to Home
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default PaymentSuccessRedirect;